// "Match to catalog" — inside the POS product edit modal.
//
// WHY HERE AND NOT A FOURTH TAB ON THE CATALOG SCREEN. Those tabs are all
// catalog-side: their left column is a delivery.Product. This queue's left
// column is a StoreProduct, and this screen already lists exactly those rows
// and already owns, right here in this modal, all three fields a match writes —
// tv_retail_price, UPC, and the link. One action, in the row already open.
//
// PREVIEW THEN CONFIRM. The endpoint is dry-run unless told otherwise, so the
// panel shows the exact changes before any of them happen — the same rule the
// bulk margin editor follows, and for the same reason: a write nobody previewed
// is a write nobody checked.
//
// MILLED ROWS ARE DISABLED, NOT HIDDEN. A greyed-out button carrying its reason
// explains itself; a row that silently vanishes from a list is a mystery. A
// milled product prices through its formula and must never be repriced from a
// distributor catalog.
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

interface Candidate {
  id: number;
  score: number;
  brand: string;
  display_name: string;
  weight_lbs: string | null;
  upc: string | null;
  unit_retail: string;
  retail_source: string;
  species: string;
  price_will_copy: boolean;
}

interface CandidatesResponse {
  store_product: {
    id: number; name: string; sku: string; upc: string | null;
    size_lbs: string; tv_retail_price: string | null; form: string;
    is_feed: boolean; has_formula: boolean;
  };
  blocked_reason: string | null;
  exact_match: Candidate | null;
  candidates: Candidate[];
  resale_fix: {
    needed: boolean; current_form: string; current_is_feed: boolean;
  };
}

interface ApplyResponse {
  applied: boolean;
  changes: string[];
  skips: string[];
}

interface Props {
  storeProductId: number;
  onApplied: () => void;
}

const S: Record<string, React.CSSProperties> = {
  wrap: {
    border: '1px solid #e5e7eb', borderRadius: 8, padding: 12,
    marginTop: 12, background: '#f9fafb',
  },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  title: { fontSize: 14, fontWeight: 700, margin: 0 },
  muted: { fontSize: 12, color: '#6b7280' },
  blocked: {
    fontSize: 12, color: '#92400e', background: '#fffbeb',
    border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 10px', marginTop: 8,
  },
  cand: {
    display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
    minHeight: 44, border: '1px solid #e5e7eb', borderRadius: 6,
    background: '#fff', cursor: 'pointer', marginBottom: 6, fontSize: 13,
  },
  candOn: { borderColor: '#059669', background: '#ecfdf5' },
  btn: {
    padding: '8px 14px', minHeight: 44, borderRadius: 6, fontSize: 13,
    border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
  },
  btnPrimary: {
    padding: '8px 14px', minHeight: 44, borderRadius: 6, fontSize: 13,
    border: 'none', background: '#059669', color: '#fff', fontWeight: 600,
    cursor: 'pointer',
  },
  btnDisabled: {
    padding: '8px 14px', minHeight: 44, borderRadius: 6, fontSize: 13,
    border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#9ca3af',
    cursor: 'not-allowed',
  },
  row: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10, alignItems: 'center' },
  change: { fontSize: 12, color: '#047857', margin: '2px 0' },
  skip: { fontSize: 12, color: '#b45309', margin: '2px 0' },
  exact: {
    fontSize: 12, color: '#065f46', background: '#ecfdf5',
    border: '1px solid #a7f3d0', borderRadius: 6, padding: '6px 10px', marginBottom: 8,
  },
};

const CatalogMatchPanel: React.FC<Props> = ({ storeProductId, onApplied }) => {
  const [data, setData] = useState<CandidatesResponse | null>(null);
  const [chosen, setChosen] = useState<number | null>(null);
  const [fixResale, setFixResale] = useState(false);
  const [preview, setPreview] = useState<ApplyResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.get(
        `/delivery/catalog/store-match/${storeProductId}/candidates/`);
      setData(res.data);
      // An exact UPC hit is the answer, not a candidate to weigh, so it starts
      // selected.
      if (res.data.exact_match) setChosen(res.data.exact_match.id);
      setFixResale(res.data.resale_fix.needed);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load catalog candidates.');
    } finally { setBusy(false); }
  }, [storeProductId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async (apply: boolean) => {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/delivery/catalog/store-match/apply/', {
        store_product_id: storeProductId,
        product_id: chosen,
        fix_resale: fixResale,
        apply,
      });
      setPreview(res.data);
      if (apply) onApplied();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'That did not go through.');
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div style={S.wrap}>
        <div style={S.head}>
          <div>
            <p style={S.title}>Match to catalog</p>
            <p style={S.muted}>
              Fills the TV price and the UPC from the distributor catalog.
            </p>
          </div>
          <button type="button" style={S.btn} onClick={() => setOpen(true)}>
            Find matches
          </button>
        </div>
      </div>
    );
  }

  const blocked = data?.blocked_reason;

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <p style={S.title}>Match to catalog</p>
        <button type="button" style={S.btn} onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      {busy && !data && <p style={S.muted}>Loading…</p>}
      {error && <p style={{ ...S.skip, color: '#dc2626' }}>{error}</p>}

      {blocked && <div style={S.blocked}>{blocked}</div>}

      {data && !blocked && (
        <>
          {data.exact_match && (
            <div style={S.exact}>
              Exact UPC match — this is the same bag, not a guess.
            </div>
          )}

          {data.candidates.length === 0 && !data.exact_match && (
            <p style={S.muted}>Nothing in the catalog looks like this row.</p>
          )}

          {(data.exact_match
            ? [data.exact_match,
               ...data.candidates.filter((c) => c.id !== data.exact_match!.id)]
            : data.candidates
          ).map((c) => (
            <button
              key={c.id}
              type="button"
              style={chosen === c.id ? { ...S.cand, ...S.candOn } : S.cand}
              onClick={() => { setChosen(c.id); setPreview(null); }}
            >
              <strong>{c.brand}</strong> — {c.display_name}
              <span style={S.muted}>
                {' '}· {c.weight_lbs ? `${Number(c.weight_lbs)} lb` : 'no weight'}
                {' '}· MSRP {c.unit_retail} · score {c.score}
              </span>
              {!c.price_will_copy && (
                <div style={S.skip}>
                  Links only — this row&apos;s retail is {c.retail_source}, not a
                  manufacturer MSRP, so no price will copy.
                </div>
              )}
            </button>
          ))}

          {/* Both flags together. They are wrong on the same rows for the same
              reason - default=True and default=PELLET on a hand-entered bag
              that is resale, not something we milled. */}
          {data.resale_fix.needed && (
            <label style={{ ...S.row, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={fixResale}
                onChange={(e) => { setFixResale(e.target.checked); setPreview(null); }}
              />
              Also mark as resale
              <span style={S.muted}>
                {' '}(Form {data.resale_fix.current_form} &rarr; NONE
                {data.resale_fix.current_is_feed ? ', is_feed → False' : ''})
              </span>
            </label>
          )}

          {preview && (
            <div style={{ marginTop: 10 }}>
              {preview.changes.map((c, i) => (
                <p key={i} style={S.change}>
                  {preview.applied ? '✓ ' : 'will change: '}{c}
                </p>
              ))}
              {preview.skips.map((s, i) => (
                <p key={i} style={S.skip}>kept: {s}</p>
              ))}
              {preview.changes.length === 0 && (
                <p style={S.muted}>Nothing to change — already complete.</p>
              )}
            </div>
          )}

          <div style={S.row}>
            <button
              type="button"
              style={chosen && !busy ? S.btn : S.btnDisabled}
              disabled={!chosen || busy}
              onClick={() => run(false)}
            >
              Preview
            </button>
            <button
              type="button"
              style={preview && !preview.applied && !busy
                ? S.btnPrimary : S.btnDisabled}
              disabled={!preview || preview.applied || busy}
              onClick={() => run(true)}
            >
              Apply
            </button>
            {!preview && (
              <span style={S.muted}>Preview first — nothing writes until you apply.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CatalogMatchPanel;
